'use client';

import { useCallback, useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { ArrowLeft, Clock, Bot } from 'lucide-react';
import { colors, typography } from '@/styles/theme';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageState } from '@/components/common/feedback/PageState';
import { toast } from '@/lib/toast';
import {
  robotApi,
  type RobotLifecycleEvent,
  type RobotUnit,
} from '../_lib/api';
import { errMsg } from '../_lib/errMsg';
import { Badge } from '../_lib/SimpleListPage';
import { StageActionPanel } from '../_lib/StageActionPanel';
import { QRDisplay } from '../_lib/QRScanner';

export default function RobotDetailPage() {
  const { t } = useTranslation();
  const rm = t.robotManager.detail;
  const eventLabels = t.robotManager.event as Record<string, string>;
  const params = useParams<{ id: string }>();
  const router = useRouter();
  const id = params?.id ?? '';
  const [unit, setUnit] = useState<RobotUnit | null>(null);
  const [events, setEvents] = useState<RobotLifecycleEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [u, evs] = await Promise.all([
        robotApi.get(id),
        robotApi.listEvents(id, 50),
      ]);
      setUnit(u);
      setEvents(evs);
    } catch (e: any) {
      toast.error(errMsg(e, t.robotManager.messages.loadFailed));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) reload();
  }, [id, reload]);

  if (loading && !unit) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-12 w-1/2" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }
  if (!unit) {
    return <PageState variant="empty" title={rm.notFound} />;
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: colors.bgSecondary }}>
      {/* Top bar */}
      <div
        className="bg-white border-b px-6 h-12 flex items-center justify-between"
        style={{ borderColor: colors.border }}
      >
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => router.push('/robot-manager')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {rm.backToList}
          </Button>
          <Bot className="w-5 h-5" style={{ color: colors.primary }} />
          <div>
            <h1 className={typography.h4}>{unit.ffsn}</h1>
            <span className={`${typography.caption} ml-2`}>
              {unit.model?.name ?? '-'} / {unit.sku?.code ?? '-'} · usage={unit.usageType}
            </span>
          </div>
        </div>
        {unit.snapshot && <Badge text={unit.snapshot.currentStage} />}
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_280px] gap-6 max-w-7xl">
          {/* 左：事件流时间线 */}
          <div className="space-y-3">
            <h2 className={typography.h4}>{rm.sectionEvents}</h2>
            <div
              className="rounded border p-4"
              style={{ borderColor: colors.border, backgroundColor: colors.bgPrimary }}
            >
              {events.length === 0 ? (
                <p className={typography.caption}>{rm.noEvents}</p>
              ) : (
                <ol className="relative border-l-2 pl-4 space-y-3" style={{ borderColor: colors.border }}>
                  {events.map((e) => (
                    <li key={e.id} className="relative">
                      <span
                        className="absolute w-2.5 h-2.5 rounded-full -left-[22px] top-1.5"
                        style={{ backgroundColor: colors.primary, border: `2px solid ${colors.bgPrimary}` }}
                      />
                      <div className="text-sm font-medium">{eventLabels[e.eventType] ?? e.eventType}</div>
                      {(e.fromStage || e.toStage) && (
                        <div className={typography.caption}>
                          {e.fromStage ?? '∅'} → {e.toStage ?? '∅'}
                        </div>
                      )}
                      {e.notes && <div className="text-sm mt-1" style={{ color: colors.textSecondary }}>{e.notes}</div>}
                      <div className={`${typography.caption} mt-0.5 flex items-center`}>
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(e.occurredAt).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* 中：当前阶段 Action Panel */}
          <div>
            <h2 className={`${typography.h4} mb-3`}>{rm.sectionCurrentAction}</h2>
            <StageActionPanel unit={unit} onChanged={reload} />
          </div>

          {/* 右：identity + QR 码 */}
          <div className="space-y-4">
            <div>
              <h2 className={`${typography.h4} mb-3`}>{rm.sectionIdentity}</h2>
              <div className="rounded border p-4 text-sm space-y-2" style={{ borderColor: colors.border, backgroundColor: colors.bgPrimary }}>
                <Row label="FFSN" value={unit.ffsn} mono />
                {unit.supplierSn && <Row label="Supplier SN" value={unit.supplierSn} mono />}
                {unit.placeholderSnOrig && <Row label={rm.placeholderSn} value={unit.placeholderSnOrig} mono />}
                <Row label={rm.modelLabel} value={unit.model?.name ?? '-'} />
                <Row label="SKU" value={unit.sku?.code ?? '-'} />
                <Row label={rm.usageLabel} value={unit.usageType} />
                {unit.manufactureDate && (
                  <Row label={rm.manufactureDate} value={new Date(unit.manufactureDate).toLocaleDateString()} />
                )}
                {unit.purchaseOrderId && <Row label={rm.purchaseOrderLabel} value={unit.purchaseOrderId.slice(0, 8)} mono />}
              </div>
            </div>

            <div>
              <h2 className={`${typography.h4} mb-3`}>{rm.sectionQrCode}</h2>
              <QRDisplay ffsn={unit.ffsn} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="shrink-0" style={{ color: colors.textTertiary }}>{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''} style={{ color: colors.textPrimary }}>{value}</span>
    </div>
  );
}
